
export default function RiskAnalysis() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-extrabold text-center mb-16 text-gray-900">
          The Hidden Risks of In-House Detailing
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-red-600 mb-6">
              In-House Employee Risks
            </h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-error-warning-fill text-red-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Experience Gap</h4>
                  <p className="text-gray-700">
                    Junior employees lack the 20+ years of field experience needed
                    to anticipate and prevent costly errors in high-rise
                    construction.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-money-dollar-circle-fill text-red-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Rework Costs</h4>
                  <p className="text-gray-700">
                    A single detailing error can cost $50,000-$500,000 in field
                    rework, schedule delays, and trade coordination issues.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-calendar-close-fill text-red-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    Downtime &amp; Turnover
                  </h4>
                  <p className="text-gray-700">
                    Sick days, vacations, and employee turnover create critical
                    gaps in project continuity and knowledge retention.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-user-unfollow-fill text-red-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    Single Point of Failure
                  </h4>
                  <p className="text-gray-700">
                    Relying on one person creates vulnerability. If they leave or
                    are unavailable, your entire detailing operation stops.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-scales-fill text-red-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    Liability Exposure
                  </h4>
                  <p className="text-gray-700">
                    Errors by in-house staff expose your company to direct
                    liability without the protection of professional indemnity
                    insurance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-teal-600 mb-6">
              Dinari Designs Protection
            </h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-star-fill text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    Proven Expertise
                  </h4>
                  <p className="text-gray-700">
                    20+ years specializing in high-rise construction detailing
                    with a track record of 50+ successful projects across Vancouver
                    BC.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-shield-check-fill text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Zero Field Errors</h4>
                  <p className="text-gray-700">
                    Our rigorous quality control process and experienced team
                    ensure accurate detailing that eliminates costly rework.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-calendar-check-fill text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    Continuous Availability
                  </h4>
                  <p className="text-gray-700">
                    24/7 service with no downtime. Your project never stops because
                    we're always available when you need us.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-team-fill text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Team Redundancy</h4>
                  <p className="text-gray-700">
                    Multiple experienced professionals ensure continuity. No
                    single point of failure means uninterrupted service.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-file-shield-fill text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    Professional Insurance
                  </h4>
                  <p className="text-gray-700">
                    Full professional liability coverage protects your project
                    and company from detailing-related risks and claims.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
